import type { GroupCreateSchemaOutput } from '@repo/schemas/group/groupCreate';
import type { GroupEditSchemaOutput } from '@repo/schemas/group/groupEdit';
import type { GetGroupMeetingsAttendance, GroupPrivate, GroupPublic } from '@repo/types/groups';
import type { MeetingPublic } from '@repo/types/meetings';
import type { Pagination, PaginationWithName, PaginationWithSort } from '@repo/types/pagination';
import type { PostPublic } from '@repo/types/posts';
import type { QuizPublic } from '@repo/types/quiz';
import type { UserPublic } from '@repo/types/users';
import type { MakeOptionalExcept } from '@repo/types/utils';
import type { AxiosResponse } from 'axios';

import { QueryClient } from '@tanstack/react-query';

import type { FileSystemItems } from '#file-system/fileSystemService';
import type { GetMeetingsParams } from '#meeting/meetingService';

import { axiosInstance } from '#api-client';

export class GroupService {
  static async getGroups(params?: PaginationWithName): Promise<AxiosResponse<GroupPublic[]>> {
    return await axiosInstance.get('/api/group/all', {
      params: params,
    });
  }

  static async getAssignedGroups(params?: PaginationWithName): Promise<AxiosResponse<GroupPrivate[]>> {
    return await axiosInstance.get('/api/group/assigned', {
      params: params,
    });
  }

  static async getGroup(id?: number): Promise<AxiosResponse<GroupPrivate>> {
    return await axiosInstance.get(`/api/group/${id}`);
  }

  static async getGroupUsers(id?: number): Promise<AxiosResponse<Array<GroupUserPublic>>> {
    return await axiosInstance.get(`/api/group/${id}/users`);
  }

  static async getGroupAttendance(id?: number): Promise<AxiosResponse<GetGroupMeetingsAttendance>> {
    return await axiosInstance.get(`/api/group/${id}/attendance`);
  }

  static async getGroupMeetings(params: GetMeetingsParams): Promise<AxiosResponse<MeetingPublic[]>> {
    const { id, ...rest } = params;
    return await axiosInstance.get(`/api/group/${id}/meetings`, {
      params: rest,
    });
  }

  static async getGroupSharedFiles(id?: number): Promise<AxiosResponse<FileSystemItems>> {
    return await axiosInstance.get(`/api/group/${id}/fs/shared`);
  }

  static async getGroupQuizzes(id?: number, pagination?: Pagination): Promise<AxiosResponse<Array<QuizPublic>>> {
    return await axiosInstance.get(`/api/group/${id}/quiz`, {
      params: pagination,
    });
  }

  static async addGroup(data: GroupCreateData): Promise<AxiosResponse<GroupPrivate>> {
    return await axiosInstance.post('/api/group', data);
  }

  static async editGroup(data: GroupEditData): Promise<AxiosResponse<GroupPrivate>> {
    const { id, ...rest } = data;
    return await axiosInstance.put(`/api/group/${id}`, rest);
  }

  static async deleteGroup(id: number): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete(`/api/group/${id}`);
  }

  static async postGroupAvatar(data: GroupAvatarUploadData): Promise<AxiosResponse<string>> {
    const { b64, id } = data;
    const formData = new FormData();
    formData.append('avatar', b64);
    formData.append('id', id.toString());
    return await axiosInstance.post(`/api/group/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static async deleteGroupAvatar(id: number): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete(`/api/group/${id}/avatar`);
  }

  static async getGroupPosts(id: number, params?: PaginationWithSort): Promise<AxiosResponse<Array<PostPublic>>> {
    return await axiosInstance.get(`/api/group/${id}/posts`, {
      params: params,
    });
  }
}

export type GroupCreateData = GroupCreateSchemaOutput;

export type GroupEditData = MakeOptionalExcept<GroupEditSchemaOutput, 'id'>;

export interface GroupAvatarUploadData {
  b64: Blob;
  id: number;
}

export interface getGroupPrefetchParams {
  queryClient: QueryClient;
  id: number;
  initialData: GroupPrivate;
}

export interface GroupUserPublic extends UserPublic {
  chatId?: number;
}
